import { postMiniCartEmailForm } from '@src/api/cart';
import { MiniCartContext, MiniCartContextActionTypeEnum } from '@src/app/mini-cart-context';
import { validateTextField } from '@src/app/mini-cart-helpers';
import Button from '@src/components/button/Button';
import { Textbox, TextboxModel } from '@src/components/form/Textbox';
import { SERVER_ERROR_MESSAGE_FALLBACK } from '@src/constants';
import { BasedModel, getModifiers } from '@src/helpers/getModifiers';
import { FormEvent, useContext } from 'react';
import { z } from 'zod';

interface MiniCartEmailFormModel extends BasedModel {
  title?: string;
  firstNameInput?: TextboxModel;
  lastNameInput?: TextboxModel;
  emailNameInput?: TextboxModel;
  companyInput?: TextboxModel;
  submitButtonText?: string;
  submitButtonPendingText?: string;
  emailFormAction?: string;
}

const MiniCartEmailForm = (model: MiniCartEmailFormModel) => {
  const styleModifiers = getModifiers(model, 'fm-o-mini-cart-email-form');
  const { title, emailNameInput, lastNameInput, firstNameInput, submitButtonText, emailFormAction, submitButtonPendingText, companyInput } = model;
  const { state: contextState, dispatch } = useContext(MiniCartContext);
  const { emailFormData, emailFormProcessing } = contextState || {};

  // declare first name schema
  let firstNameScheme = z.string();

  if (firstNameInput?.required) {
    firstNameScheme = firstNameScheme.min(1, { message: firstNameInput.requiredMessage });
  }

  // declare last name schema
  let lastNameSchema = z.string();

  if (lastNameInput?.required) {
    lastNameSchema = lastNameSchema.min(1, { message: lastNameInput.requiredMessage });
  }

  // declare email schema
  let emailSchema = z.string();

  if (emailNameInput?.required) {
    emailSchema = emailSchema.min(1, { message: emailNameInput.requiredMessage });
  }

  // declare company schema
  let companySchema = z.string();

  if (companyInput?.required) {
    companySchema = companySchema.min(1, { message: companyInput.requiredMessage });
  }

  const handleFirstNameInput = (event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const firstNameValidate = validateTextField(value, firstNameScheme);

    dispatch?.({
      type: MiniCartContextActionTypeEnum.SetEmailFormData,
      payload: { ...emailFormData, firstName: value, firstNameErrorMessage: firstNameValidate.valid ? '' : firstNameValidate.message },
    });
  };

  const handleLastNameInput = (event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const lastNameValidate = validateTextField(value, lastNameSchema);

    dispatch?.({
      type: MiniCartContextActionTypeEnum.SetEmailFormData,
      payload: { ...emailFormData, lastName: value, lastNameErrorMessage: lastNameValidate.valid ? '' : lastNameValidate.message },
    });
  };

  const handleEmailInput = (event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const emailValidate = validateTextField(value, emailSchema);

    dispatch?.({
      type: MiniCartContextActionTypeEnum.SetEmailFormData,
      payload: { ...emailFormData, email: value, emailErrorMessage: emailValidate.valid ? '' : emailValidate.message },
    });
  };

  const handleCompanyInput = (event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const companyValidate = validateTextField(value, companySchema);

    dispatch?.({
      type: MiniCartContextActionTypeEnum.SetEmailFormData,
      payload: { ...emailFormData, company: value, companyErrorMessage: companyValidate.valid ? '' : companyValidate.message },
    });
  };

  const handleButtonClick = () => {
    const firstNameValidate = validateTextField(emailFormData?.firstName || '', firstNameScheme);
    const lastNameValidate = validateTextField(emailFormData?.lastName || '', lastNameSchema);
    const emailValidate = validateTextField(emailFormData?.email || '', emailSchema);
    const companyValidate = validateTextField(emailFormData?.company || '', companySchema);

    if (!firstNameValidate.valid || !lastNameValidate.valid || !emailValidate.valid || !companyValidate.valid) {
      dispatch?.({
        type: MiniCartContextActionTypeEnum.SetEmailFormData,
        payload: {
          ...emailFormData,
          firstNameErrorMessage: firstNameValidate.message,
          lastNameErrorMessage: lastNameValidate.message,
          emailErrorMessage: emailValidate.message,
          companyErrorMessage: companyValidate.message,
        },
      });

      return;
    }

    dispatch?.({
      type: MiniCartContextActionTypeEnum.SetEmailFormData,
      payload: { ...emailFormData, firstNameErrorMessage: '', lastNameErrorMessage: '', emailErrorMessage: '', companyErrorMessage: '' },
    });

    if (!emailFormAction) {
      return;
    }

    dispatch?.({ type: MiniCartContextActionTypeEnum.SetEmailFormProcessing, payload: true });

    postMiniCartEmailForm(emailFormAction, {
      email: emailFormData?.email,
      firstName: emailFormData?.firstName,
      lastName: emailFormData?.lastName,
      company: emailFormData?.company,
    })
      .then((data) => {
        if (!data.result && !data.success && !data.successfully) {
          dispatch?.({
            type: MiniCartContextActionTypeEnum.SetServerErrorMessage,
            payload: data.error || window.serverErrorMessageFallback || SERVER_ERROR_MESSAGE_FALLBACK,
          });

          return;
        }

        dispatch?.({ type: MiniCartContextActionTypeEnum.SetShowThankyouMessage, payload: true });
      })
      .catch(() => {
        dispatch?.({
          type: MiniCartContextActionTypeEnum.SetServerErrorMessage,
          payload: window.serverErrorMessageFallback || SERVER_ERROR_MESSAGE_FALLBACK,
        });
      })
      .finally(() => dispatch?.({ type: MiniCartContextActionTypeEnum.SetEmailFormProcessing, payload: false }));
  };

  return (
    <div className={`${styleModifiers} ${!!emailFormProcessing ? 'submitting' : ''}`}>
      {title && <div className="fm-o-mini-cart-email-form__title">{title}</div>}

      <div className="fm-o-mini-cart-email-form__inputs">
        {firstNameInput && (
          <Textbox
            {...firstNameInput}
            value={emailFormData?.firstName}
            handleInput={handleFirstNameInput}
            hasError={!!emailFormData?.firstNameErrorMessage}
            message={emailFormData?.firstNameErrorMessage}
          />
        )}

        {lastNameInput && (
          <Textbox
            {...lastNameInput}
            value={emailFormData?.lastName}
            handleInput={handleLastNameInput}
            hasError={!!emailFormData?.lastNameErrorMessage}
            message={emailFormData?.lastNameErrorMessage}
          />
        )}

        {companyInput && (
          <Textbox
            {...companyInput}
            value={emailFormData?.company}
            handleInput={handleCompanyInput}
            hasError={!!emailFormData?.companyErrorMessage}
            message={emailFormData?.companyErrorMessage}
          />
        )}

        {emailNameInput && (
          <Textbox
            {...emailNameInput}
            value={emailFormData?.email}
            handleInput={handleEmailInput}
            hasError={!!emailFormData?.emailErrorMessage}
            message={emailFormData?.emailErrorMessage}
          />
        )}

        {submitButtonText && (
          <Button
            text={!emailFormProcessing ? submitButtonText : submitButtonPendingText || 'Sending...'}
            raw={true}
            mainClass="fm-o-mini-cart-email-form__submit-btn"
            styleModifier={['fm-a-button--blue', 'fm-a-button--small-x-padding']}
            onClick={handleButtonClick}
            disabled={!!emailFormProcessing}
          />
        )}
      </div>
    </div>
  );
};

export default MiniCartEmailForm;
